import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

// ─── Access token: только в памяти ────────────────────────────────────
// refresh_token живёт в httpOnly cookie, выставленной бэкендом —
// JS его не видит, поэтому XSS не может его украсть. Короткоживущий
// access_token держим тут, на полной перезагрузке страницы он теряется
// и восстанавливается одним POST /auth/refresh (см. AuthContext.bootstrap).

let accessTokenInMemory: string | null = null;

export function getAccessToken(): string | null {
  return accessTokenInMemory;
}

export function setAccessToken(token: string | null) {
  accessTokenInMemory = token;
}

export function clearAuthStorage() {
  accessTokenInMemory = null;
}

// ─── Axios instances ──────────────────────────────────────────────────

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  // Обязательно для отправки httpOnly refresh cookie на /auth/refresh.
  withCredentials: true,
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ─── Helpers ──────────────────────────────────────────────────────────

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const isAuthEndpoint = (url?: string) => {
  if (!url) return false;
  return url.includes('/auth/login') || url.includes('/auth/refresh');
};

// ─── Request: добавляем Bearer ────────────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response: на 401 → refresh через cookie, повторяем запрос ────────

let refreshPromise: Promise<string | null> | null = null;

/**
 * Просит бэк обменять refresh-cookie на свежий access_token. Возвращает
 * новый access_token (и кладёт в память) либо null, если cookie невалидный.
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await refreshClient.post<{ access_token: string }>(
      '/auth/refresh',
      {},
    );
    const token = res.data?.access_token || null;
    setAccessToken(token);
    return token;
  } catch {
    setAccessToken(null);
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) return Promise.reject(error);

    const originalRequest = error.config as
      | (AxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (!originalRequest) return Promise.reject(error);

    if (isAuthEndpoint(originalRequest.url)) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    try {
      const token = await refreshPromise;
      if (!token) {
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      }
      originalRequest.headers = originalRequest.headers || {};
      (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  },
);

export default apiClient;
